import {
  ENPSAnalyticsFilterView,
  ENPSOverviewView,
  ENPSTrendPointView,
  ENPSSegmentOptionsView,
  ENPSThemeSignalView,
  ENPSThemeSignalMonthView,
  ENPSFocusAreaView,
  ENPSFocusAreaInput,
  ENPSFocusAreaUpdateInput,
  ENPSLearningView,
  ENPSLearningBulkInput,
  ENPSExportInput,
  ENPSThemeCatalogView,
  ENPSThemeCatalogInput,
  ENPSThemeCatalogUpdateInput,
  ENPSCoverageView,
  ENPSThemeBriefingView,
  ENPSExtractionResultView,
  ENPSFocusAreaSuggestionsView,
  ENPSActionSuggestionsView,
  ENPSEmployeeBriefingView,
  ENPSEmployeeBriefingDraftView,
} from "@/models/preformanceManagement";
import request from "../../utils/request";
import { Result } from "@/models/general";

export async function getENPSOverview(
  filters: ENPSAnalyticsFilterView
): Promise<Result<ENPSOverviewView>> {
  try {
    const params: Record<string, string> = {};
    if (filters.dateRange) params.dateRange = filters.dateRange;
    if (filters.department) params.department = filters.department;
    if (filters.location) params.location = filters.location;

    const response = await request.get<Result<ENPSOverviewView>>(
      "ENPSAnalytics/Overview",
      { params }
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching eNPS overview:", error);
    throw error;
  }
}

export async function getENPSTrends(
  filters: ENPSAnalyticsFilterView
): Promise<Result<ENPSTrendPointView[]>> {
  try {
    const params: Record<string, string> = {};
    if (filters.dateRange) params.dateRange = filters.dateRange;
    if (filters.department) params.department = filters.department;
    if (filters.location) params.location = filters.location;

    const response = await request.get<Result<ENPSTrendPointView[]>>(
      "ENPSAnalytics/Trends",
      { params }
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching eNPS trends:", error);
    throw error;
  }
}

export async function getENPSSegmentOptions(): Promise<
  Result<ENPSSegmentOptionsView>
> {
  try {
    const response = await request.get<Result<ENPSSegmentOptionsView>>(
      "ENPSAnalytics/SegmentOptions"
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching eNPS segment options:", error);
    throw error;
  }
}

// Theme Signals
/**
 * Every survey month that has theme signals, newest first. Separate from getENPSThemeSignals, which
 * stays scoped to the newest month so the headline reading of the dashboard does not change.
 */
export async function getENPSThemeSignalHistory(): Promise<
  Result<ENPSThemeSignalMonthView[]>
> {
  try {
    const response = await request.get<Result<ENPSThemeSignalMonthView[]>>(
      "ENPSAnalytics/ThemeSignalHistory"
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching eNPS theme signal history:", error);
    throw error;
  }
}

export async function getENPSThemeSignals(): Promise<
  Result<ENPSThemeSignalView[]>
> {
  try {
    const response = await request.get<Result<ENPSThemeSignalView[]>>(
      "ENPSAnalytics/ThemeSignals"
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching eNPS theme signals:", error);
    throw error;
  }
}

// Theme Catalog
export async function getENPSThemeCatalog(
  includeInactive = false
): Promise<Result<ENPSThemeCatalogView[]>> {
  try {
    const response = await request.get<Result<ENPSThemeCatalogView[]>>(
      "ENPSAnalytics/ThemeCatalog",
      { params: { includeInactive } }
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching eNPS theme catalog:", error);
    throw error;
  }
}

export async function createENPSThemeCatalogEntry(
  input: ENPSThemeCatalogInput
): Promise<Result<ENPSThemeCatalogView>> {
  try {
    const response = await request.post<Result<ENPSThemeCatalogView>>(
      "ENPSAnalytics/ThemeCatalog",
      input
    );
    return response.data;
  } catch (error) {
    console.error("Error creating eNPS theme catalog entry:", error);
    throw error;
  }
}

export async function updateENPSThemeCatalogEntry(
  id: number,
  input: ENPSThemeCatalogUpdateInput
): Promise<Result<ENPSThemeCatalogView>> {
  try {
    const response = await request.put<Result<ENPSThemeCatalogView>>(
      `ENPSAnalytics/ThemeCatalog/${id}`,
      input
    );
    return response.data;
  } catch (error) {
    console.error("Error updating eNPS theme catalog entry:", error);
    throw error;
  }
}

export async function deactivateENPSThemeCatalogEntry(
  id: number
): Promise<Result<boolean>> {
  try {
    const response = await request.delete<Result<boolean>>(
      `ENPSAnalytics/ThemeCatalog/${id}`
    );
    return response.data;
  } catch (error) {
    console.error("Error deactivating eNPS theme catalog entry:", error);
    throw error;
  }
}

// Opinion Coverage
export async function getENPSCoverage(): Promise<Result<ENPSCoverageView>> {
  try {
    const response = await request.get<Result<ENPSCoverageView>>(
      "ENPSAnalytics/Coverage"
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching eNPS opinion coverage:", error);
    throw error;
  }
}

// Theme Briefing (read only, never generates)
export async function getENPSThemeBriefing(): Promise<
  Result<ENPSThemeBriefingView>
> {
  try {
    const response = await request.get<Result<ENPSThemeBriefingView>>(
      "ENPSAnalytics/ThemeBriefing"
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching eNPS theme briefing:", error);
    throw error;
  }
}

// Employee-facing briefing
export async function getENPSEmployeeBriefing(): Promise<
  Result<ENPSEmployeeBriefingView>
> {
  try {
    const response = await request.get<Result<ENPSEmployeeBriefingView>>(
      "ENPSEmployeeBriefing"
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching eNPS employee briefing:", error);
    throw error;
  }
}

// AI actions, each runs only on an explicit user action
export async function extractENPSThemes(): Promise<
  Result<ENPSExtractionResultView>
> {
  try {
    const response = await request.post<Result<ENPSExtractionResultView>>(
      "ENPSAi/ExtractThemes"
    );
    return response.data;
  } catch (error) {
    console.error("Error extracting eNPS themes:", error);
    throw error;
  }
}

export async function scoreENPSCoverage(): Promise<Result<ENPSCoverageView>> {
  try {
    const response = await request.post<Result<ENPSCoverageView>>(
      "ENPSAi/ScoreCoverage"
    );
    return response.data;
  } catch (error) {
    console.error("Error scoring eNPS opinion coverage:", error);
    throw error;
  }
}

export async function generateENPSThemeBriefing(): Promise<
  Result<ENPSThemeBriefingView>
> {
  try {
    const response = await request.post<Result<ENPSThemeBriefingView>>(
      "ENPSAi/ThemeBriefing"
    );
    return response.data;
  } catch (error) {
    console.error("Error generating eNPS theme briefing:", error);
    throw error;
  }
}

export async function suggestENPSFocusAreas(): Promise<
  Result<ENPSFocusAreaSuggestionsView>
> {
  try {
    const response = await request.post<Result<ENPSFocusAreaSuggestionsView>>(
      "ENPSAi/SuggestFocusAreas"
    );
    return response.data;
  } catch (error) {
    console.error("Error suggesting eNPS focus areas:", error);
    throw error;
  }
}

export async function suggestENPSActions(): Promise<
  Result<ENPSActionSuggestionsView>
> {
  try {
    const response = await request.post<Result<ENPSActionSuggestionsView>>(
      "ENPSAi/SuggestActions"
    );
    return response.data;
  } catch (error) {
    console.error("Error suggesting eNPS actions:", error);
    throw error;
  }
}

export async function draftENPSEmployeeBriefing(): Promise<
  Result<ENPSEmployeeBriefingDraftView>
> {
  try {
    const response = await request.post<Result<ENPSEmployeeBriefingDraftView>>(
      "ENPSAi/DraftEmployeeBriefing"
    );
    return response.data;
  } catch (error) {
    console.error("Error drafting eNPS employee briefing:", error);
    throw error;
  }
}

// Focus Areas
export async function getENPSFocusAreas(): Promise<
  Result<ENPSFocusAreaView[]>
> {
  try {
    const response = await request.get<Result<ENPSFocusAreaView[]>>(
      "ENPSAnalytics/FocusAreas"
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching eNPS focus areas:", error);
    throw error;
  }
}

export async function createENPSFocusArea(
  input: ENPSFocusAreaInput
): Promise<Result<ENPSFocusAreaView>> {
  try {
    const response = await request.post<Result<ENPSFocusAreaView>>(
      "ENPSAnalytics/FocusAreas",
      input
    );
    return response.data;
  } catch (error) {
    console.error("Error creating eNPS focus area:", error);
    throw error;
  }
}

export async function updateENPSFocusArea(
  id: number,
  input: ENPSFocusAreaUpdateInput
): Promise<Result<ENPSFocusAreaView>> {
  try {
    const response = await request.put<Result<ENPSFocusAreaView>>(
      `ENPSAnalytics/FocusAreas/${id}`,
      input
    );
    return response.data;
  } catch (error) {
    console.error("Error updating eNPS focus area:", error);
    throw error;
  }
}

// Focus History
export async function getENPSFocusHistory(): Promise<
  Result<ENPSFocusAreaView[]>
> {
  try {
    const response = await request.get<Result<ENPSFocusAreaView[]>>(
      "ENPSAnalytics/FocusHistory"
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching eNPS focus history:", error);
    throw error;
  }
}

// Learnings
export async function getENPSLearnings(): Promise<
  Result<ENPSLearningView[]>
> {
  try {
    const response = await request.get<Result<ENPSLearningView[]>>(
      "ENPSAnalytics/Learnings"
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching eNPS learnings:", error);
    throw error;
  }
}

export async function saveENPSLearnings(
  input: ENPSLearningBulkInput
): Promise<Result<ENPSLearningView[]>> {
  try {
    const response = await request.put<Result<ENPSLearningView[]>>(
      "ENPSAnalytics/Learnings",
      input
    );
    return response.data;
  } catch (error) {
    console.error("Error saving eNPS learnings:", error);
    throw error;
  }
}

// Export
export async function exportENPSDashboard(
  input: ENPSExportInput
): Promise<void> {
  try {
    const response = await request.post<ArrayBuffer>(
      "ENPSAnalytics/Export",
      input,
      {
        responseType: "blob",
        headers: { "Content-Type": "application/json" },
      }
    );

    const cd: string | undefined =
      response.headers?.["content-disposition"] ||
      response.headers?.["Content-Disposition"];
    let fileName: string | undefined;
    if (cd) {
      const utf8Match = cd.match(/filename\*=UTF-8''([^;\n]+)/i);
      if (utf8Match?.[1]) {
        try {
          fileName = decodeURIComponent(utf8Match[1]);
        } catch {
          /* noop */
        }
      }
      if (!fileName) {
        const simpleMatch = cd.match(/filename="?([^";\n]+)"?/i);
        if (simpleMatch?.[1]) fileName = simpleMatch[1];
      }
    }

    if (!fileName) {
      const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      const ext = input.format === "pdf" ? "pdf" : "csv";
      fileName = `eNPS-Report-${ts}.${ext}`;
    }

    const mime =
      input.format === "pdf" ? "application/pdf" : "text/csv";
    const url = window.URL.createObjectURL(
      new Blob([response.data], { type: mime })
    );
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }, 0);
  } catch (error) {
    console.error("Error exporting eNPS dashboard:", error);
    throw error;
  }
}
